// apps/widget/src/screens/lead-capture.screen.tsx
//
// The report is free. This is where the PDF and the booking are gated — and
// "No thanks" is a real button, because a gate that traps people converts once
// and is resented afterwards.

import { useState } from 'react';
import { api } from '../lib/api';
import { getLocale, t } from '../lib/i18n';
import { send } from '../lib/postmessage';
import { useScan } from '../state';
import { PhoneField, defaultCountry, phoneE164, type PhoneValue } from '../components/phone-field';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function LeadCaptureScreen() {
  const { state, go } = useScan();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<PhoneValue>(() => ({
    country: defaultCountry(state.config.tenant.country),
    number: '',
  }));
  const [emailTouched, setEmailTouched] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [marketing, setMarketing] = useState(state.marketingOptIn);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Either is enough; whichever is filled in must be right.
  const emailFilled = email.trim() !== '';
  const phoneFilled = phone.number.trim() !== '';
  const emailOk = EMAIL.test(email.trim());
  const e164 = phoneE164(phone);
  const valid = (emailFilled || phoneFilled) && (!emailFilled || emailOk) && (!phoneFilled || e164 !== null);
  const emailProblem =
    (emailTouched || attempted) && emailFilled && !emailOk
      ? t('booking.emailInvalid')
      : attempted && !emailFilled && !phoneFilled
        ? t('lead.needOne')
        : null;

  const submit = async (): Promise<void> => {
    setAttempted(true);
    if (!valid) return;
    setBusy(true);
    setError(null);

    try {
      const result = await api.lead({
        ...(state.scanId ? { scanId: state.scanId } : {}),
        ...(name ? { firstName: name } : {}),
        ...(emailFilled ? { email: email.trim() } : {}),
        ...(e164 ? { phone: e164 } : {}),
        marketingOptIn: marketing,
        ...(Object.keys(state.intake).length ? { intake: state.intake } : {}),
        locale: getLocale(),
      });

      if (result.leadId) send('gs:lead:captured', { leadId: result.leadId });
      setSent(true);
    } catch {
      // Their details are worth another attempt — never a dead end.
      setError('We could not send that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="gs-stack gs-centred">
        <div className="gs-tick" aria-hidden="true">✓</div>
        <h1 className="gs-title">{t('lead.sent')}</h1>
        <button className="gs-btn gs-btn--primary" onClick={() => go('booking')}>
          {t('recommendations.book')}
        </button>
      </div>
    );
  }

  return (
    <div className="gs-stack">
      <h1 className="gs-title">{t('lead.title')}</h1>
      <p className="gs-muted">{t('lead.subtitle')}</p>

      <label className="gs-field">
        <span className="gs-field-label">{t('lead.name')}</span>
        <input className="gs-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="given-name" />
      </label>

      <label className="gs-field">
        <span className="gs-field-label">{t('lead.email')}</span>
        <input
          className="gs-input"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          autoComplete="email"
          aria-invalid={emailProblem ? true : undefined}
        />
        {emailProblem && (
          <span className="gs-field-error" role="alert">
            {emailProblem}
          </span>
        )}
      </label>

      <PhoneField label={t('lead.phone')} value={phone} onChange={setPhone} showError={attempted} optional />

      {/* Separate from the biometric consent given earlier, and captured with
          its own proof. Enforced server-side in the send path. */}
      <label className="gs-check gs-check--muted">
        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
        <span>{t('consent.marketing', { clinic: state.config.tenant.name })}</span>
      </label>

      {error && (
        <p className="gs-error" role="alert">
          {error}
        </p>
      )}

      <button className="gs-btn gs-btn--primary" disabled={busy} onClick={() => void submit()}>
        {t('lead.send')}
      </button>

      <button className="gs-btn gs-btn--ghost" onClick={() => go('recommendations')}>
        {t('lead.skip')}
      </button>
    </div>
  );
}
